/**
 * Los hooks de datos del catalogo publico.
 *
 * Viven aca y no en `lib/queries.ts` por la misma razon que los de compras: son
 * de este modulo. La puerta al backend sigue siendo `api`.
 *
 * TODO lo que escribe invalida el resumen y los productos: los contadores de
 * cada rubro («12 de 38 publicados · 9 con foto») se recalculan en la base, y
 * mantenerlos a mano en el cliente seria tener dos verdades.
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ErrorApi } from "@/lib/api";
import { claves } from "@/lib/queries";
import { clavesTienda } from "@/components/tienda/consultas";
import type { Oferta, OfertaEnvio, Pagina, Producto, ResumenPublicacion, Rubro } from "@/lib/tipos";

export const clavesPublicacion = {
  raiz: ["publicacion"] as const,
  resumen: ["publicacion", "resumen"] as const,
  ofertas: ["publicacion", "ofertas"] as const,
  secciones: ["publicacion", "secciones"] as const,
};

function invalidarTodo(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: clavesPublicacion.raiz });
  // La vista previa del home lee la tienda tal como la ve el cliente.
  void qc.invalidateQueries({ queryKey: clavesTienda.raiz });
  void qc.invalidateQueries({ queryKey: claves.productos });
  void qc.invalidateQueries({ queryKey: claves.rubros });
}

/** El estado del catalogo: rubros con contadores, totales, contacto y el link. */
export function useResumenPublicacion(): UseQueryResult<ResumenPublicacion, ErrorApi> {
  return useQuery<ResumenPublicacion, ErrorApi>({
    queryKey: clavesPublicacion.resumen,
    queryFn: ({ signal }) => api.get<ResumenPublicacion>("/catalogo-publico/resumen", { signal }),
  });
}

/**
 * Los productos de un rubro.
 *
 * `limite: 500` porque el rubro mas grande tiene 50 y el tope de la ruta es
 * 500: paginar acá obligaría a publicar en dos veces lo que es una sola
 * decision. `activo` sin pedirlo: el backend ya devuelve solo esos.
 */
export function useProductosDeRubro(
  rubroId: number | undefined,
  habilitado = true,
): UseQueryResult<Pagina<Producto>, ErrorApi> {
  const filtros = { rubro_id: rubroId ?? 0, limite: 500 };
  return useQuery<Pagina<Producto>, ErrorApi>({
    queryKey: claves.listaProductos(filtros),
    queryFn: ({ signal }) => api.get<Pagina<Producto>>("/productos", { params: filtros, signal }),
    enabled: habilitado && typeof rubroId === "number" && rubroId > 0,
  });
}

/**
 * Los productos de varios rubros, con la misma clave que `useProductosDeRubro`
 * (lo que ya pidio una fila no se vuelve a pedir). Es para saber, con un filtro
 * puesto, que rubros tienen algo: `null` es que ese rubro todavia no llego.
 */
export function useProductosDeRubros(
  rubroIds: readonly number[],
  habilitado: boolean,
): Map<number, Producto[] | null> {
  const resultados = useQueries({
    queries: rubroIds.map((id) => {
      const filtros = { rubro_id: id, limite: 500 };
      return {
        queryKey: claves.listaProductos(filtros),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          api.get<Pagina<Producto>>("/productos", { params: filtros, signal }),
        enabled: habilitado,
      };
    }),
  });
  return new Map(rubroIds.map((id, i) => [id, resultados[i]?.data?.filas ?? null]));
}

export interface CambioPublicacion {
  ids?: number[];
  rubro_id?: number;
  publicado: boolean;
}

export function usePublicarProductos(): UseMutationResult<
  { afectados: number },
  ErrorApi,
  CambioPublicacion
> {
  const qc = useQueryClient();
  return useMutation<{ afectados: number }, ErrorApi, CambioPublicacion>({
    mutationFn: (datos) => api.post<{ afectados: number }>("/productos/publicar", datos),
    onSuccess: () => invalidarTodo(qc),
  });
}

/**
 * Meter o sacar tarjetas del home (0092). Va por ids porque las secciones del
 * home no son rubros: es la pantalla la que sabe que productos tiene cada una.
 */
export function useMarcarHome(): UseMutationResult<
  { afectados: number },
  ErrorApi,
  { ids: number[]; en_home: boolean }
> {
  const qc = useQueryClient();
  return useMutation<{ afectados: number }, ErrorApi, { ids: number[]; en_home: boolean }>({
    mutationFn: (datos) => api.post<{ afectados: number }>("/productos/home", datos),
    onSuccess: () => invalidarTodo(qc),
  });
}

/**
 * Agregar productos a la tienda desde el Home: los publica, los pone en el
 * home, prende su rubro y, con `seccion_id`, los suma a esa seccion propia.
 */
export function useAgregarALaTienda(): UseMutationResult<
  { afectados: number },
  ErrorApi,
  { ids: number[]; seccion_id?: number | null }
> {
  const qc = useQueryClient();
  return useMutation<
    { afectados: number },
    ErrorApi,
    { ids: number[]; seccion_id?: number | null }
  >({
    mutationFn: (datos) => api.post<{ afectados: number }>("/productos/a-la-tienda", datos),
    onSuccess: () => invalidarTodo(qc),
  });
}

/** Una seccion propia del home (0104), como la administra el sistema. */
export interface SeccionTienda {
  id: number;
  titulo: string;
  /** Todos sus productos, visibles o no, en orden. */
  productos: { producto_id: number; lugar: number }[];
}

/** Las secciones propias, con sus productos en orden. */
export function useSeccionesTienda(): UseQueryResult<SeccionTienda[], ErrorApi> {
  return useQuery<SeccionTienda[], ErrorApi>({
    queryKey: clavesPublicacion.secciones,
    queryFn: async ({ signal }) =>
      (await api.get<{ filas: SeccionTienda[] }>("/secciones-tienda", { signal }))?.filas ?? [],
  });
}

export function useCrearSeccion(): UseMutationResult<SeccionTienda, ErrorApi, string> {
  const qc = useQueryClient();
  return useMutation<SeccionTienda, ErrorApi, string>({
    mutationFn: (titulo) => api.post<SeccionTienda>("/secciones-tienda", { titulo }),
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useRenombrarSeccion(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; titulo: string }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: number; titulo: string }>({
    mutationFn: ({ id, titulo }) => api.patch(`/secciones-tienda/${id}`, { titulo }),
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useBorrarSeccion(): UseMutationResult<unknown, ErrorApi, number> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, number>({
    mutationFn: (id) => api.delete(`/secciones-tienda/${id}`),
    onSuccess: () => invalidarTodo(qc),
  });
}

/**
 * La lista entera de una seccion propia, en orden: cada tarjeta con los ids
 * de todos sus productos.
 */
export function useGuardarSeccion(): UseMutationResult<
  { afectados: number },
  ErrorApi,
  { id: number; tarjetas: number[][] }
> {
  const qc = useQueryClient();
  return useMutation<{ afectados: number }, ErrorApi, { id: number; tarjetas: number[][] }>({
    mutationFn: ({ id, tarjetas }) =>
      api.put<{ afectados: number }>(`/secciones-tienda/${id}/productos`, { tarjetas }),
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useActualizarRubro(): UseMutationResult<
  Rubro,
  ErrorApi,
  { id: number; cambios: { publicado?: boolean; orden?: number } }
> {
  const qc = useQueryClient();
  return useMutation<
    Rubro,
    ErrorApi,
    { id: number; cambios: { publicado?: boolean; orden?: number } }
  >({
    mutationFn: ({ id, cambios }) => api.patch<Rubro>(`/rubros/${id}`, cambios),
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useReordenarRubros(): UseMutationResult<Pagina<Rubro>, ErrorApi, number[]> {
  const qc = useQueryClient();
  return useMutation<Pagina<Rubro>, ErrorApi, number[]>({
    mutationFn: (ids) => api.post<Pagina<Rubro>>("/rubros/orden", { ids }),
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useSubirImagen(): UseMutationResult<
  Producto,
  ErrorApi,
  { id: number; formulario: FormData }
> {
  const qc = useQueryClient();
  return useMutation<Producto, ErrorApi, { id: number; formulario: FormData }>({
    mutationFn: ({ id, formulario }) =>
      api.subir<Producto>("PUT", `/productos/${id}/imagen`, formulario),
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useQuitarImagen(): UseMutationResult<Producto, ErrorApi, number> {
  const qc = useQueryClient();
  return useMutation<Producto, ErrorApi, number>({
    mutationFn: (id) => api.delete<Producto>(`/productos/${id}/imagen`),
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useSubirLogo(): UseMutationResult<{ logo_url: string | null }, ErrorApi, FormData> {
  const qc = useQueryClient();
  return useMutation<{ logo_url: string | null }, ErrorApi, FormData>({
    mutationFn: (formulario) =>
      api.subir<{ logo_url: string | null }>("PUT", "/config/logo", formulario),
    onSuccess: () => {
      invalidarTodo(qc);
      void qc.invalidateQueries({ queryKey: claves.config });
    },
  });
}

export function useQuitarLogo(): UseMutationResult<{ logo_url: string | null }, ErrorApi, void> {
  const qc = useQueryClient();
  return useMutation<{ logo_url: string | null }, ErrorApi, void>({
    mutationFn: () => api.delete<{ logo_url: string | null }>("/config/logo"),
    onSuccess: () => {
      invalidarTodo(qc);
      void qc.invalidateQueries({ queryKey: claves.config });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Ofertas
// ─────────────────────────────────────────────────────────────────────────────
//
// Se administran desde esta pantalla porque son parte de lo que ve el cliente,
// igual que los rubros y el logo. No son productos y no viven en Catalogo: no
// tienen stock, ni costo, ni codigo propio (migracion 0060).

/** Todas, activas o no: esta pantalla las administra, no las muestra. */
export function useOfertas(): UseQueryResult<Oferta[], ErrorApi> {
  return useQuery<Oferta[], ErrorApi>({
    queryKey: clavesPublicacion.ofertas,
    queryFn: async ({ signal }) => {
      const r = await api.get<{ filas: Oferta[] }>("/ofertas", { signal });
      return r?.filas ?? [];
    },
  });
}

function invalidarOfertas(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: clavesPublicacion.ofertas });
  void qc.invalidateQueries({ queryKey: clavesTienda.ofertas });
}

export function useCrearOferta(): UseMutationResult<Oferta, ErrorApi, OfertaEnvio> {
  const qc = useQueryClient();
  return useMutation<Oferta, ErrorApi, OfertaEnvio>({
    mutationFn: (datos) => api.post<Oferta>("/ofertas", datos),
    onSuccess: () => invalidarOfertas(qc),
  });
}

export function useActualizarOferta(): UseMutationResult<
  Oferta,
  ErrorApi,
  { id: number; cambios: Partial<OfertaEnvio> }
> {
  const qc = useQueryClient();
  return useMutation<Oferta, ErrorApi, { id: number; cambios: Partial<OfertaEnvio> }>({
    mutationFn: ({ id, cambios }) => api.patch<Oferta>(`/ofertas/${id}`, cambios),
    onSuccess: () => invalidarOfertas(qc),
  });
}

/** La foto propia de una oferta (0110). Devuelve la oferta con su `imagen_url` nueva. */
export function useSubirImagenOferta(): UseMutationResult<
  Oferta,
  ErrorApi,
  { id: number; formulario: FormData }
> {
  const qc = useQueryClient();
  return useMutation<Oferta, ErrorApi, { id: number; formulario: FormData }>({
    mutationFn: ({ id, formulario }) =>
      api.subir<Oferta>("PUT", `/ofertas/${id}/imagen`, formulario),
    onSuccess: () => invalidarOfertas(qc),
  });
}

export function useQuitarImagenOferta(): UseMutationResult<Oferta, ErrorApi, number> {
  const qc = useQueryClient();
  return useMutation<Oferta, ErrorApi, number>({
    mutationFn: (id) => api.delete<Oferta>(`/ofertas/${id}/imagen`),
    onSuccess: () => invalidarOfertas(qc),
  });
}

export function useBorrarOferta(): UseMutationResult<{ ok: boolean }, ErrorApi, number> {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ErrorApi, number>({
    mutationFn: (id) => api.delete<{ ok: boolean }>(`/ofertas/${id}`),
    onSuccess: () => invalidarOfertas(qc),
  });
}
